import { useCallback, useContext, useEffect, useState, type JSX } from "react";
import dayjs from "dayjs";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { Context } from "@/lib/Context";
import Tabs from "@/components/tabs/Tabs";
import Module from "@/components/module/Module";
import AINote from "@/components/ai-note/AINote";
import ChatInput from "@/components/chat-input/ChatInput";
import ContentLayout from "@/layouts/content/ContentLayout";
import AgreementsForm from "@/components/agreements-form/AgreementsForm";
import { DataGrid, MemoizedDataGrid } from "@/components/data-grid/DataGrid";
import { universeState, agreementsState, whiteState, userState } from "@/lib/store";
import SubscriptionBanner from "@/components/subscription-banner/SubscriptionBanner";
import OptionsPlatformOfferHost, { OptionsUpsellMessage } from "@/components/options-platform/offer-host/OptionsPlatformOfferHost";
import { useLegacyOptionsVisible, useOptionsUiRedirect } from "src/hooks/useOptionsUiRedirect";
import { legacyOptionsCounterpart } from "src/hooks/optionsUiRouting";
import { useRowBatcher } from "src/hooks/useRowBatcher";
import { getOptionsInterestingFlow, getOptionFlowSentiment, getOptionsNote } from "@/controllers/options";
import PageSeo from "@/components/page-seo/PageSeo";

import styles from "./index.module.scss";

const tabs = [
    { title: "OVERVIEW", value: "overview" },
    { title: "INTERESTING OPTIONS FLOW", value: "interesting-options-flow" },
    { title: "OPTION FLOW SENTIMENT", value: "option-flow-sentiment" },
];

export const VisitorsMessage = ({ color }: { color?: string }) => {
    const router = useRouter();
    return (
        <div className={styles.form}>
            <Typography variant="body2">Options data is restricted and cannot be viewed without having an account. Please create a free account to access options data.</Typography>
            <Button variant="contained" size="small" sx={{ mt: 1, color }} onClick={() => router.push("/login?register=true")}>
                Create free account
            </Button>
        </div>
    );
};

export const AgreeTermsMessage = ({ color, setForm }: { color?: string; setForm?: (v: boolean) => void }) => (
    <div className={styles.form}>
        <Typography variant="body2">To view options data from the Options Price Reporting Authority, you must agree to OPRA&apos;s terms and conditions. Please fill in the form to access the data immediately.</Typography>
        <Button variant="contained" size="small" sx={{ mt: 1, color }} onClick={() => setForm(true)}>
            Open form
        </Button>
    </div>
);

const OptionsPage: NextPage = (props) => {
    const router = useRouter();
    // Users entitled to the new platform are forwarded there…
    useOptionsUiRedirect("legacy", router.isReady ? legacyOptionsCounterpart(router.query.tab) : null);
    // …and must not glimpse the old interface while that happens: the
    // redirect can only fire once the user payload lands, so hold the
    // legacy content back until we know this user belongs here.
    const legacyVisible = useLegacyOptionsVisible();
    const { ortexWebSocket } = useContext(Context);

    const agreements = useAtomValue(agreementsState);
    const universe = useAtomValue(universeState);
    const white = useAtomValue(whiteState);
    const user = useAtomValue(userState);

    const [form, setForm] = useState(false);
    const [row, setRow] = useState<any[]>();
    const [optionsFlowFilters, setOptionsFlowFilters] = useState<JSX.Element>();
    const [flowSentimentFilters, setFlowSentimentFilters] = useState<JSX.Element>();
    const [rangeSelection, setRangeSelection] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<string>("overview");
    const [optionsNote, setOptionsNote] = useState<string | null>(null);
    const [optionsNoteObfuscated, setOptionsNoteObfuscated] = useState(false);
    const [isLoadingNote, setIsLoadingNote] = useState<boolean>(true);

    const handleRangeChange = (_: React.MouseEvent<HTMLElement>, range: number) => {
        setRangeSelection(range);
    };

    const flowSentimentRangeSelector = (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ToggleButtonGroup color="primary" exclusive aria-label="toggle-buttons" onChange={handleRangeChange} value={rangeSelection} size="small">
                <ToggleButton value={0}>TODAY</ToggleButton>
                <ToggleButton value={1}>YESTERDAY</ToggleButton>
                <ToggleButton value={7}>1 WEEK</ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );

    const activeHelp =
        activeTab === "overview" ? { moduleId: "options_note", modulePage: "V2_options" } : activeTab === "interesting-options-flow" ? { moduleId: "options_flow", modulePage: "V2_options" } : { moduleId: "flow_sentiment", modulePage: "V2_options" };
    const activeFilters =
        activeTab === "overview" ? undefined : activeTab === "interesting-options-flow" ? (
            optionsFlowFilters
        ) : (
            <>
                {flowSentimentRangeSelector}
                {flowSentimentFilters}
            </>
        );

    const allowFullScreen = activeTab === "option-flow-sentiment";

    const sendMessage = useCallback(() => {
        ortexWebSocket.send(JSON.stringify({ type: "subscribe", identifier: "#OIT" }));
    }, [agreements, ortexWebSocket]);

    const queueRow = useRowBatcher(setRow);

    const addRow = useCallback(
        ({ data }: MessageEvent<any>) => {
            const { "#OIT": OIT } = JSON.parse(data);

            if (OIT)
                queueRow({
                    callPut: OIT.OT,
                    expiryDate: dayjs.utc(OIT.EXP, "YYMMDD").valueOf(),
                    price: OIT.avg_price,
                    reason: OIT.trigger_reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                    size: OIT.total_volume,
                    stock: OIT.stock_name,
                    strike: OIT.strike,
                    ticker: OIT.UL,
                    time: dayjs.utc(OIT.T).valueOf(),
                    totalValue: OIT.total_value,
                    securityId: OIT.security_id || OIT.securityId,
                    company: OIT.stock_name,
                    id: OIT.uuid,
                });
        },
        [queueRow]
    );

    useEffect(() => {
        if (agreements?.find(({ code }) => code === "OPRA") && ortexWebSocket) {
            ortexWebSocket.readyState === 1 ? sendMessage() : ortexWebSocket.addEventListener("open", sendMessage);
            ortexWebSocket.addEventListener("message", addRow);
        }

        return () => {
            if (agreements?.find(({ code }) => code === "OPRA") && ortexWebSocket) {
                ortexWebSocket.removeEventListener("open", sendMessage);
                ortexWebSocket.removeEventListener("message", addRow);
                if (ortexWebSocket.readyState === 1) ortexWebSocket.send(JSON.stringify({ type: "unsubscribe", identifier: "#OIT" }));
            }
        };
    }, [agreements, ortexWebSocket]);

    const handleChange = (event: React.MouseEvent<HTMLElement>, tab: string) => {
        if (tab) {
            setActiveTab(tab);
            addParamToURL(tab);
        }
    };
    const addParamToURL = (tabName) => {
        router.push(
            {
                pathname: "/options",
                query: { tab: tabName },
            },
            undefined,
            { shallow: true }
        );
    };

    useEffect(() => {
        if (router.query.tab !== "" && router.query.tab !== undefined) {
            // @ts-ignore
            setActiveTab(router.query.tab);
        }
    }, [router.query.tab]);

    useEffect(() => {
        if (user.settings.module_settings["/options->options"]?.activeTab) {
            setActiveTab(user.settings.module_settings["/options->options"].activeTab);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "overview") {
            getOptionsNote()
                .then((data) => {
                    setOptionsNote(data.response ?? null);
                    setOptionsNoteObfuscated(!!data.obfuscated);
                    setIsLoadingNote(false);
                })
                .catch((err) => {
                    console.error("Error fetching options note:", err);
                    setIsLoadingNote(false);
                });
        }
    }, [activeTab]);

    const renderData = () => {
        switch (activeTab) {
            case "overview":
                return (
                    <Module noHeader>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                overflow: "hidden",
                                position: "relative",
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    width: "100%",
                                    paddingX: { xs: 0.5, md: "calc((100% - 700px) / 2)", xl: "17.5%" },
                                    overflowY: "auto",
                                    paddingBottom: 10,
                                    transition: "flex 0.3s ease-out",
                                }}
                            >
                                {optionsNoteObfuscated ? <SubscriptionBanner /> : <AINote content={optionsNote} isLoading={isLoadingNote} />}
                            </Box>
                            {!optionsNoteObfuscated && <ChatInput placeholder="Ask more about options data..." startingModel="options_note" />}
                        </Box>
                    </Module>
                );
            case "interesting-options-flow":
                return (
                    <Module noHeader>
                        {agreements?.find(({ code }) => code === "OPRA") ? (
                            universe && <DataGrid id={universe?.id} fetcher={getOptionsInterestingFlow} key="options_flow" extraRow={row} setFilters={setOptionsFlowFilters} tableId="options_flow" noCalendarPresets />
                        ) : user.subscription_plan === "ORTEX Visitor" ? (
                            <VisitorsMessage color={white} />
                        ) : user.options_legacy ? (
                            <AgreeTermsMessage color={white} setForm={setForm} />
                        ) : (
                            <OptionsUpsellMessage color={white} />
                        )}
                    </Module>
                );
            case "option-flow-sentiment":
                return (
                    <Module noHeader>
                        {agreements?.find(({ code }) => code === "OPRA") ? (
                            universe && (
                                <MemoizedDataGrid
                                    id={universe?.id}
                                    fetcher={getOptionFlowSentiment}
                                    key="flow_sentiment"
                                    setFilters={setFlowSentimentFilters}
                                    data={{ days_back: rangeSelection }}
                                    reset={{ setter: setRangeSelection, defaultValue: 0 }}
                                />
                            )
                        ) : user.subscription_plan === "ORTEX Visitor" ? (
                            <VisitorsMessage color={white} />
                        ) : user.options_legacy ? (
                            <AgreeTermsMessage color={white} setForm={setForm} />
                        ) : (
                            <OptionsUpsellMessage color={white} />
                        )}
                    </Module>
                );
        }
    };

    return (
        <>
            <PageSeo
                title="Options Flow & Unusual Options Activity | ORTEX"
                pageName="Options"
                canonicalPath="/options"
                description="Live options data, flow, gamma exposure and unusual activity for stocks across global exchanges."
                keywords="Options, Stock Options, Interesting Option Flow, Option Sentiment, Option Chain"
            />

            <ContentLayout title={"Options Flow and Unusual Activity"} allowFullscreen={allowFullScreen} help={activeHelp} filters={legacyVisible ? activeFilters : undefined}>
                <>
                    {legacyVisible && (
                        <>
                            <OptionsPlatformOfferHost />
                            <Box sx={{ mx: "auto", height: "40px" }}>
                                <Tabs tabs={tabs} activeTab={activeTab} module={"/options"} route={"options"} setState={setActiveTab} handleChange={handleChange} setDefault />
                            </Box>
                            <Box sx={{ height: "calc(100% - 40px)" }}>{renderData()}</Box>
                            <AgreementsForm isOpen={form} onClose={setForm} />
                        </>
                    )}
                </>
            </ContentLayout>
        </>
    );
};

export default OptionsPage;
